<template>
    <div class="cp-dash" :class="{ 'cp-dash--dark': dark }">
        <div class="cp-dash__wrap">
            <div v-if="viewer.isPreview" class="cp-preview-banner">
                <div>
                    <strong>{{ $t('corporate.previewAs.banner') }}:</strong>
                    <span>{{ viewer.label }}</span>
                </div>
                <button type="button" class="cp-preview-banner__exit" @click="exitPreview">
                    {{ $t('corporate.previewAs.exit') }}
                </button>
            </div>

            <header class="cp-welcome">
                <div class="cp-welcome__identity">
                    <div class="cp-welcome__avatar" aria-hidden="true">{{ initials }}</div>
                    <div class="cp-welcome__copy">
                        <p class="cp-welcome__eyebrow">{{ $t('student.corporatePortal.education') }}</p>
                        <h1>{{ greeting }}</h1>
                        <p class="cp-welcome__lead">
                            {{ $t('student.corporatePortal.welcomeLead') }}
                        </p>
                    </div>
                </div>
                <div class="cp-welcome__ring" :aria-label="$t('student.corporatePortal.overallProgress')">
                    <svg viewBox="0 0 96 96" class="cp-welcome__ring-svg">
                        <circle class="cp-welcome__ring-track" cx="48" cy="48" r="40" />
                        <circle
                            class="cp-welcome__ring-value"
                            cx="48"
                            cy="48"
                            r="40"
                            :style="ringStyle"
                        />
                    </svg>
                    <div class="cp-welcome__ring-label">
                        <strong>{{ overallProgress }}%</strong>
                        <span>{{ $t('student.corporatePortal.overallShort') }}</span>
                    </div>
                </div>
            </header>

            <section class="cp-kpis" :aria-label="$t('student.corporatePortal.coursesSummary')">
                <button
                    v-for="kpi in kpiCards"
                    :key="kpi.key"
                    type="button"
                    class="cp-kpi"
                    :class="{ 'is-active': statusFilter === kpi.key }"
                    :aria-pressed="statusFilter === kpi.key"
                    @click="setStatusFilter(kpi.key)"
                >
                    <div class="cp-kpi__icon" :class="`cp-kpi__icon--${kpi.tone}`">
                        <fa :icon="kpi.icon" />
                    </div>
                    <div class="cp-kpi__body">
                        <span>{{ kpi.label }}</span>
                        <strong>{{ kpi.value }}</strong>
                    </div>
                </button>
            </section>

            <section v-if="keepWatching" class="cp-continue">
                <div class="cp-section-head">
                    <h2>{{ $t('student.dashboard.keepWatching') }}</h2>
                    <p>{{ $t('student.corporatePortal.continueWatchingLead') }}</p>
                </div>
                <button type="button" class="cp-continue__card" @click="openKeepWatching">
                    <div
                        class="cp-continue__media"
                        :style="keepWatchingImageStyle"
                    >
                        <span class="cp-continue__play" aria-hidden="true">
                            <fa icon="fa-play" />
                        </span>
                    </div>
                    <div class="cp-continue__body">
                        <span class="cp-continue__tag">{{ $t('student.courses.inProgress') }}</span>
                        <h3>{{ keepWatching.title }}</h3>
                        <p v-if="keepWatching.description">{{ keepWatching.description }}</p>
                        <span class="cp-continue__cta">{{ $t('student.keepWatching.continueFromWhere') }}</span>
                    </div>
                </button>
            </section>

            <section class="cp-courses">
                <div class="cp-section-head cp-section-head--row">
                    <div>
                        <h2>{{ $t('student.dashboard.myCourses') }}</h2>
                        <p>{{ coursesSectionLead }}</p>
                    </div>
                    <label class="cp-search">
                        <fa icon="fa-magnifying-glass" />
                        <input
                            v-model="search"
                            type="search"
                            :placeholder="$t('student.courses.search') + '...'"
                        />
                    </label>
                </div>

                <div class="cp-courses__controls">
                    <div class="cp-tabs" role="tablist" :aria-label="$t('student.corporatePortal.catalogTabs')">
                        <button
                            v-for="tab in catalogTabs"
                            :key="tab.key"
                            type="button"
                            role="tab"
                            class="cp-tabs__btn"
                            :class="{ 'is-active': catalogTab === tab.key }"
                            :aria-selected="catalogTab === tab.key"
                            @click="catalogTab = tab.key"
                        >
                            {{ tab.label }}
                            <span class="cp-tabs__count">{{ tab.count }}</span>
                        </button>
                    </div>
                    <label class="cp-completion">
                        <span class="cp-completion__label">{{ $t('student.corporatePortal.completionFilter') }}</span>
                        <select v-model="completionFilter">
                            <option value="all">{{ $t('student.corporatePortal.filterAll') }}</option>
                            <option value="done">{{ $t('student.corporatePortal.filterFinished') }}</option>
                            <option value="open">{{ $t('student.corporatePortal.filterUnfinished') }}</option>
                        </select>
                    </label>
                </div>

                <div v-if="!hasReady" class="cp-state">
                    <span class="loader home"></span>
                </div>

                <div v-else-if="!hasAssignedContent" class="cp-state">
                    <div class="cp-state__icon"><fa icon="fa-graduation-cap" /></div>
                    <h3>{{ $t('student.corporatePortal.noAssignedCourses') }}</h3>
                    <p>
                        {{ $t('student.corporatePortal.noAssignedCoursesLead') }}
                    </p>
                </div>

                <div v-else-if="!hasVisibleCatalog" class="cp-state">
                    <h3>{{ $t('student.courses.empty') }}</h3>
                    <p>{{ emptyFilterMessage }}</p>
                    <button
                        v-if="hasActiveFilters"
                        type="button"
                        class="cp-state__clear"
                        @click="clearFilters"
                    >
                        {{ $t('student.corporatePortal.clearFilters') }}
                    </button>
                </div>

                <div v-else class="cp-course-list">
                    <template v-if="showPackages">
                    <section
                        v-for="group in visiblePackages"
                        :key="`package-${group.id}`"
                        class="cp-package"
                        :class="{ 'is-open': isPackageOpen(group.id) }"
                    >
                        <button
                            type="button"
                            class="cp-package__toggle"
                            :aria-expanded="isPackageOpen(group.id)"
                            @click="togglePackage(group.id)"
                        >
                            <span class="cp-package__copy">
                                <span class="cp-package__title-row">
                                    <strong>{{ group.name }}</strong>
                                    <span class="cp-chip" :class="group.mode === 'path' ? 'is-path' : 'is-group'">
                                        {{ group.mode === 'path' ? $t('corporate.groups.modePath') : $t('corporate.groups.modeGroup') }}
                                    </span>
                                    <span class="cp-chip is-pending">{{ group.entries.length }}</span>
                                </span>
                                <span class="cp-package__lead">
                                    {{
                                        group.mode === 'path'
                                            ? $t('corporate.groups.sectionLeadPath')
                                            : $t('corporate.groups.sectionLeadGroup')
                                    }}
                                </span>
                            </span>
                            <fa :icon="isPackageOpen(group.id) ? 'fa-chevron-up' : 'fa-chevron-down'" />
                        </button>
                        <div
                            v-if="packageCertificateAction(group)"
                            class="cp-package__certificate"
                        >
                            <button
                                v-if="packageCertificateAction(group) === 'issued'"
                                type="button"
                                class="cp-course__btn cp-course__btn--certificate"
                                @click="viewCertificate(packageCertificateItem(group))"
                            >
                                {{ $t('student.corporatePortal.viewCertificate') }}
                                <fa icon="fa-award" />
                            </button>
                            <button
                                v-else-if="packageCertificateAction(group) === 'eligible'"
                                type="button"
                                class="cp-course__btn cp-course__btn--certificate"
                                :disabled="isIssuingCertificate(packageCertificateItem(group))"
                                @click="generateCertificate(packageCertificateItem(group))"
                            >
                                {{ $t('student.corporatePortal.generateCertificate') }}
                                <fa icon="fa-award" />
                            </button>
                        </div>

                        <div v-show="isPackageOpen(group.id)" class="cp-package__courses">
                            <article
                                v-for="entry in group.entries"
                                :key="entry.key"
                                class="cp-course"
                                :class="{ 'is-locked': entry.locked }"
                                @click="openPackageCourse(entry)"
                            >
                                <div class="cp-course__media" :style="imageStyle(entry.card)">
                                    <div v-if="!courseImage(entry.card)" class="cp-course__placeholder">
                                        <fa icon="fa-graduation-cap" />
                                    </div>
                                </div>
                                <div class="cp-course__main">
                                    <div class="cp-course__title-row">
                                        <h3>{{ courseName(entry.card) }}</h3>
                                        <span v-if="entry.locked" class="cp-chip is-locked">
                                            {{ $t('corporate.groups.locked') }}
                                        </span>
                                        <span v-else class="cp-chip" :class="statusClass(entry.card)">
                                            {{ statusLabel(entry.card) }}
                                        </span>
                                        <span
                                            v-if="courseExtra(entry.card).mandatory"
                                            class="cp-chip is-mandatory"
                                        >
                                            {{ $t('student.corporatePortal.mandatory') }}
                                        </span>
                                    </div>
                                    <p v-if="entry.locked && entry.lockReason" class="cp-course__lock">
                                        {{ entry.lockReason }}
                                    </p>
                                    <p v-else-if="courseDescription(entry.card)" class="cp-course__desc">
                                        {{ courseDescription(entry.card) }}
                                    </p>
                                    <div class="cp-course__org">
                                        <div class="cp-course__org-item">
                                            <span>{{ $t('student.corporatePortal.areaSector') }}</span>
                                            <strong>{{ courseExtra(entry.card).sector }}</strong>
                                        </div>
                                        <div class="cp-course__org-item">
                                            <span>{{ $t('corporate.users.responsible') }}</span>
                                            <strong>{{ courseExtra(entry.card).responsible }}</strong>
                                        </div>
                                        <div class="cp-course__org-item">
                                            <span>{{ $t('student.corporatePortal.role') }}</span>
                                            <strong>{{ courseExtra(entry.card).role }}</strong>
                                        </div>
                                        <div class="cp-course__org-item">
                                            <span>{{ $t('student.corporatePortal.deadline') }}</span>
                                            <strong>{{ courseExtra(entry.card).deadline }}</strong>
                                        </div>
                                    </div>
                                    <div class="cp-course__progress">
                                        <div class="cp-course__progress-head">
                                            <span>{{ $t('student.dashboard.progress') }}</span>
                                            <strong>{{ courseProgress(entry.card) }}%</strong>
                                        </div>
                                        <div class="cp-course__track">
                                            <div
                                                class="cp-course__fill"
                                                :class="progressTone(entry.card)"
                                                :style="{ width: `${courseProgress(entry.card)}%` }"
                                            />
                                        </div>
                                    </div>
                                    <div class="cp-course__footer">
                                        <div class="cp-course__meta">
                                            <span v-if="moduleQty(entry.card) != null">
                                                <fa icon="fa-book" />
                                                {{ moduleQty(entry.card) }} {{ $t('student.corporatePortal.modulesShort') }}
                                            </span>
                                            <span v-if="lessonQty(entry.card) != null">
                                                <fa icon="fa-play" />
                                                {{ lessonQty(entry.card) }} {{ $t('student.corporatePortal.lessonsShort') }}
                                            </span>
                                            <span>
                                                <fa icon="fa-calendar" />
                                                {{ courseExtra(entry.card).assignedAt }}
                                            </span>
                                        </div>
                                        <button
                                            v-if="certificateAction(entry.card) === 'issued'"
                                            type="button"
                                            class="cp-course__btn cp-course__btn--certificate"
                                            @click.stop="viewCertificate(entry.card)"
                                        >
                                            {{ $t('student.corporatePortal.viewCertificate') }}
                                            <fa icon="fa-award" />
                                        </button>
                                        <button
                                            v-else-if="certificateAction(entry.card) === 'eligible'"
                                            type="button"
                                            class="cp-course__btn cp-course__btn--certificate"
                                            :disabled="isIssuingCertificate(entry.card)"
                                            @click.stop="generateCertificate(entry.card)"
                                        >
                                            {{ $t('student.corporatePortal.generateCertificate') }}
                                            <fa icon="fa-award" />
                                        </button>
                                        <button
                                            v-else-if="courseProgress(entry.card) < 100"
                                            type="button"
                                            class="cp-course__btn"
                                            :disabled="entry.locked"
                                            @click.stop="openPackageCourse(entry)"
                                        >
                                            <template v-if="entry.locked">
                                                {{ $t('corporate.groups.locked') }}
                                            </template>
                                            <template v-else>
                                                {{ courseProgress(entry.card) > 0 ? $t('student.courses.continue') : $t('student.courses.start') }}
                                                <fa icon="fa-arrow-right" />
                                            </template>
                                        </button>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </section>
                    </template>
                    <template v-if="showSimpleCourses">
                    <article
                        v-for="item in filteredCourses"
                        :key="courseKey(item)"
                        class="cp-course"
                        @click="openCourse(item)"
                    >
                        <div class="cp-course__media" :style="imageStyle(item)">
                            <div v-if="!courseImage(item)" class="cp-course__placeholder">
                                <fa icon="fa-graduation-cap" />
                            </div>
                        </div>

                        <div class="cp-course__main">
                            <div class="cp-course__title-row">
                                <h3>{{ courseName(item) }}</h3>
                                <span class="cp-chip" :class="statusClass(item)">
                                    {{ statusLabel(item) }}
                                </span>
                                <span
                                    v-if="courseExtra(item).mandatory"
                                    class="cp-chip is-mandatory"
                                >
                                    {{ $t('student.corporatePortal.mandatory') }}
                                </span>
                            </div>

                            <p v-if="courseDescription(item)" class="cp-course__desc">
                                {{ courseDescription(item) }}
                            </p>

                            <div class="cp-course__org">
                                <div class="cp-course__org-item">
                                    <span>{{ $t('student.corporatePortal.areaSector') }}</span>
                                    <strong>{{ courseExtra(item).sector }}</strong>
                                </div>
                                <div class="cp-course__org-item">
                                    <span>{{ $t('corporate.users.responsible') }}</span>
                                    <strong>{{ courseExtra(item).responsible }}</strong>
                                </div>
                                <div class="cp-course__org-item">
                                    <span>{{ $t('student.corporatePortal.role') }}</span>
                                    <strong>{{ courseExtra(item).role }}</strong>
                                </div>
                                <div class="cp-course__org-item">
                                    <span>{{ $t('student.corporatePortal.deadline') }}</span>
                                    <strong>{{ courseExtra(item).deadline }}</strong>
                                </div>
                            </div>

                            <div class="cp-course__progress">
                                <div class="cp-course__progress-head">
                                    <span>{{ $t('student.dashboard.progress') }}</span>
                                    <strong>{{ courseProgress(item) }}%</strong>
                                </div>
                                <div class="cp-course__track">
                                    <div
                                        class="cp-course__fill"
                                        :class="progressTone(item)"
                                        :style="{ width: `${courseProgress(item)}%` }"
                                    />
                                </div>
                            </div>

                            <div class="cp-course__footer">
                                <div class="cp-course__meta">
                                    <span v-if="moduleQty(item) != null">
                                        <fa icon="fa-book" />
                                        {{ moduleQty(item) }} {{ $t('student.corporatePortal.modulesShort') }}
                                    </span>
                                    <span v-if="lessonQty(item) != null">
                                        <fa icon="fa-play" />
                                        {{ lessonQty(item) }} {{ $t('student.corporatePortal.lessonsShort') }}
                                    </span>
                                    <span>
                                        <fa icon="fa-calendar" />
                                        {{ courseExtra(item).assignedAt }}
                                    </span>
                                </div>

                                <button
                                    v-if="certificateAction(item) === 'issued'"
                                    type="button"
                                    class="cp-course__btn cp-course__btn--certificate"
                                    @click.stop="viewCertificate(item)"
                                >
                                    {{ $t('student.corporatePortal.viewCertificate') }}
                                    <fa icon="fa-award" />
                                </button>
                                <button
                                    v-else-if="certificateAction(item) === 'eligible'"
                                    type="button"
                                    class="cp-course__btn cp-course__btn--certificate"
                                    :disabled="isIssuingCertificate(item)"
                                    @click.stop="generateCertificate(item)"
                                >
                                    {{ $t('student.corporatePortal.generateCertificate') }}
                                    <fa icon="fa-award" />
                                </button>
                                <button
                                    v-else-if="courseProgress(item) < 100"
                                    type="button"
                                    class="cp-course__btn"
                                    @click.stop="openCourse(item)"
                                >
                                    {{ courseProgress(item) > 0 ? $t('student.courses.continue') : $t('student.courses.start') }}
                                    <fa icon="fa-arrow-right" />
                                </button>
                            </div>
                        </div>
                    </article>
                    </template>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import lessonApi from '@/utils/portal/lesson';
import apiCertificates, { isTruthyFlag } from '@/utils/portal/certificate.js';
import { moduleTokenFromEnrol } from '@/utils/portal/courseToken';
import { mapState, mapActions } from 'vuex';
import { toast } from 'vue3-toastify';
import { getPortalViewerContext } from '@/utils/corporate/portalViewer';
import { corporateI18n } from '@/i18n/mixins';

export default {
    name: 'CorporateDashboard',
    mixins: [corporateI18n],
    props: {
        data: { type: Array, default: () => [] },
        groupCourses: { type: Array, default: () => [] },
        hasReady: { type: Boolean, default: false },
    },
    data() {
        return {
            search: '',
            statusFilter: 'total',
            catalogTab: 'all',
            completionFilter: 'all',
            keepWatching: null,
            openPackages: {},
            certificateById: {},
            certificateByName: {},
            issuingCourseId: null,
        };
    },
    computed: {
        ...mapState(['dark', 'user', 'token', 'previewAs']),
        viewer() {
            return getPortalViewerContext();
        },
        courses() {
            return Array.isArray(this.data) ? this.data : [];
        },
        greeting() {
            void this.$i18n.locale;
            const name = this.user?.name || this.user?.nome || '';
            const first = String(name).trim().split(/\s+/)[0];
            return first
                ? this.$t('student.corporatePortal.greeting', { name: first })
                : this.$t('student.corporatePortal.greetingShort');
        },
        initials() {
            const name = this.user?.name || this.user?.nome || '';
            const parts = String(name).trim().split(/\s+/).filter(Boolean);
            if (!parts.length) return 'WC';
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        },
        stats() {
            let completed = 0;
            let inProgress = 0;
            let notStarted = 0;
            this.courses.forEach((item) => {
                const p = this.courseProgress(item);
                if (p >= 100) completed += 1;
                else if (p > 0) inProgress += 1;
                else notStarted += 1;
            });
            return {
                total: this.courses.length,
                completed,
                inProgress,
                notStarted,
            };
        },
        kpiCards() {
            void this.$i18n.locale;
            return [
                { key: 'total', label: this.$t('student.corporatePortal.kpiMyCourses'), value: this.stats.total, icon: 'fa-graduation-cap', tone: 'primary' },
                { key: 'progress', label: this.$t('student.corporatePortal.kpiInProgress'), value: this.stats.inProgress, icon: 'fa-play', tone: 'violet' },
                { key: 'done', label: this.$t('student.corporatePortal.kpiCompleted'), value: this.stats.completed, icon: 'fa-circle-check', tone: 'green' },
                { key: 'pending', label: this.$t('student.corporatePortal.kpiNotStarted'), value: this.stats.notStarted, icon: 'fa-clock', tone: 'amber' },
            ];
        },
        activeKpiLabel() {
            return this.kpiCards.find((kpi) => kpi.key === this.statusFilter)?.label || this.$t('student.corporatePortal.kpiMyCourses');
        },
        coursesSectionLead() {
            void this.$i18n.locale;
            if (this.statusFilter === 'total') {
                return this.$t('student.corporatePortal.coursesLeadDefault');
            }
            return this.$t('student.corporatePortal.coursesLeadFilter', { filter: this.activeKpiLabel });
        },
        emptyFilterMessage() {
            void this.$i18n.locale;
            if (this.search.trim()) return this.$t('student.corporatePortal.emptySearch');
            if (this.statusFilter === 'progress') return this.$t('student.corporatePortal.emptyInProgress');
            if (this.statusFilter === 'done' || this.completionFilter === 'done') {
                return this.$t('student.corporatePortal.emptyCompleted');
            }
            if (this.statusFilter === 'pending') return this.$t('student.corporatePortal.emptyNotStarted');
            if (this.completionFilter === 'open') return this.$t('student.corporatePortal.emptyUnfinished');
            if (this.catalogTab === 'packages') return this.$t('student.corporatePortal.emptyPackages');
            if (this.catalogTab === 'simple') return this.$t('student.corporatePortal.emptySimple');
            return this.$t('student.courses.empty');
        },
        hasAssignedContent() {
            return this.courses.length > 0 || (this.groupCourses || []).length > 0;
        },
        catalogTabs() {
            void this.$i18n.locale;
            const packages = (this.groupCourses || []).length;
            const simple = this.courses.length;
            return [
                { key: 'all', label: this.$t('student.corporatePortal.tabAll'), count: simple + packages },
                { key: 'simple', label: this.$t('student.corporatePortal.tabSimple'), count: simple },
                { key: 'packages', label: this.$t('student.corporatePortal.tabPackages'), count: packages },
            ];
        },
        showPackages() {
            return this.catalogTab !== 'simple';
        },
        showSimpleCourses() {
            return this.catalogTab !== 'packages';
        },
        hasVisibleCatalog() {
            const packages = this.showPackages ? this.visiblePackages.length : 0;
            const simple = this.showSimpleCourses ? this.filteredCourses.length : 0;
            return packages + simple > 0;
        },
        hasActiveFilters() {
            return this.statusFilter !== 'total'
                || this.completionFilter !== 'all'
                || Boolean(this.search.trim());
        },
        overallProgress() {
            if (!this.courses.length) return 0;
            const sum = this.courses.reduce((acc, item) => acc + this.courseProgress(item), 0);
            return Math.round(sum / this.courses.length);
        },
        ringStyle() {
            const circumference = 2 * Math.PI * 40;
            const offset = circumference - (this.overallProgress / 100) * circumference;
            return {
                strokeDasharray: `${circumference} ${circumference}`,
                strokeDashoffset: offset,
            };
        },
        visiblePackages() {
            const term = this.search.trim().toLowerCase();
            return (this.groupCourses || [])
                .map((group) => {
                    const entries = this.buildPackageEntries(group).filter((entry) => {
                        if (term && !String(this.courseName(entry.card)).toLowerCase().includes(term)
                            && !String(group.name || '').toLowerCase().includes(term)) {
                            return false;
                        }
                        return this.courseVisible(entry.card);
                    });
                    return { ...group, entries };
                })
                .filter((group) => group.entries.length > 0);
        },
        filteredCourses() {
            let list = this.courses.filter((item) => this.courseVisible(item));

            if (this.search.trim()) {
                const term = this.search.toLowerCase();
                list = list.filter((item) => this.courseName(item).toLowerCase().includes(term));
            }

            return list;
        },
        keepWatchingImageStyle() {
            if (!this.keepWatching?.image) return {};
            return { backgroundImage: `url('${this.keepWatching.image}')` };
        },
    },
    created() {
        this.loadKeepWatching();
        this.loadCertificateIndex();
    },
    watch: {
        previewAs: {
            deep: true,
            handler() {
                this.loadKeepWatching();
                this.loadCertificateIndex();
            },
        },
        groupCourses: {
            immediate: true,
            handler(groups) {
                const open = { ...this.openPackages };
                const startOpen = this.catalogTab !== 'packages';
                (groups || []).forEach((group) => {
                    if (open[group.id] == null) open[group.id] = startOpen;
                });
                this.openPackages = open;
            },
        },
        catalogTab(tab) {
            if (tab !== 'packages') return;
            const open = { ...this.openPackages };
            Object.keys(open).forEach((id) => {
                open[id] = false;
            });
            this.openPackages = open;
        },
    },
    methods: {
        ...mapActions([
            'storeCurrentCoursePortal',
            'storeLatestCourse',
            'clearPreviewAs',
            'storePortalCorporateLayout',
        ]),
        exitPreview() {
            this.clearPreviewAs();
            this.$router.push('/dashboard/corporativo/visao-geral');
        },
        isPackageOpen(id) {
            if (this.catalogTab === 'packages') {
                return this.openPackages[id] === true;
            }
            return this.openPackages[id] !== false;
        },
        togglePackage(id) {
            this.openPackages = {
                ...this.openPackages,
                [id]: !this.isPackageOpen(id),
            };
        },
        matchesStatusFilter(item) {
            const progress = this.courseProgress(item);
            if (this.statusFilter === 'progress') return progress > 0 && progress < 100;
            if (this.statusFilter === 'done') return progress >= 100;
            if (this.statusFilter === 'pending') return progress <= 0;
            return true;
        },
        matchesCompletion(item) {
            const progress = this.courseProgress(item);
            if (this.completionFilter === 'done') return progress >= 100;
            if (this.completionFilter === 'open') return progress < 100;
            return true;
        },
        courseVisible(item) {
            return this.matchesStatusFilter(item) && this.matchesCompletion(item);
        },
        findEnrolledCourse(item) {
            const list = this.courses;
            const byId = list.find((course) => {
                const courseId = course?.course?.id ?? course?.id;
                return courseId != null && String(courseId) === String(item.courseId);
            });
            if (byId) return byId;
            const name = String(item.courseName || '').trim().toLowerCase();
            if (!name) return null;
            return list.find((course) => {
                const courseName = String(course?.course?.name || course?.name || '').trim().toLowerCase();
                return courseName && courseName === name;
            }) || null;
        },
        buildPackageEntries(group) {
            return (group.items || []).map((item, index) => {
                const enrolled = this.findEnrolledCourse(item);
                const locked = Boolean(item.locked);
                const progress = enrolled
                    ? this.courseProgress(enrolled)
                    : Number(item.progress || 0);
                const base = enrolled || {
                    id: item.courseId,
                    progress,
                    course: {
                        id: item.courseId,
                        name: item.courseName,
                        progressPercentage: progress,
                        shortDescription: locked ? '' : (item.shortDescription || ''),
                        moduleQty: item.moduleQty ?? null,
                        lessonQty: item.lessonQty ?? null,
                        imageFinalPath: item.imageFinalPath || item.imageUrl || null,
                        imageFinalPath2: item.imageFinalPath2 || null,
                    },
                };
                const card = {
                    ...base,
                    sectorName: this.pickOrgValue(item?.sectorName, base.sectorName, base.course?.sectorName),
                    roleName: this.pickOrgValue(item?.roleName, base.roleName, base.course?.roleName),
                    responsibleName: this.pickOrgValue(
                        item?.responsibleName,
                        base.responsibleName,
                        base.course?.responsibleName,
                    ),
                    deadlineDays: this.pickOrgValue(
                        item?.deadlineDays,
                        base.deadlineDays,
                        base.course?.deadlineDays,
                    ),
                };
                return {
                    key: `${group.id}-${item.courseId || index}`,
                    locked,
                    lockReason: locked
                        ? (item.lockReason || this.$t('corporate.groups.locked'))
                        : '',
                    card,
                };
            });
        },
        openPackageCourse(entry) {
            if (!entry || entry.locked) return;
            const id = this.courseId(entry.card);
            if (id == null || id === '') return;
            this.openCourse(entry.card);
        },
        setStatusFilter(key) {
            this.statusFilter = this.statusFilter === key ? 'total' : key;
            this.$nextTick(() => {
                document.querySelector('.cp-courses')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
        },
        clearFilters() {
            this.statusFilter = 'total';
            this.completionFilter = 'all';
            this.search = '';
        },
        async loadKeepWatching() {
            if (!this.token || this.viewer.isPreview) {
                this.keepWatching = null;
                return;
            }
            try {
                const response = await lessonApi.getLatestCourse(this.token);
                const token = moduleTokenFromEnrol(response, this.user?.type);
                if (!token) return;
                this.storeLatestCourse({ latestCourse: response });
                this.storeCurrentCoursePortal({ currentCoursePortal: response });
                this.keepWatching = {
                    id: token,
                    title: response.course?.name || this.$t('student.corporatePortal.continueCourse'),
                    description: response.course?.shortDescription || '',
                    image: response.course?.imageFinalPath || response.course?.imageFinalPath2 || '',
                    raw: response,
                };
            } catch (e) {
                this.keepWatching = null;
            }
        },
        async loadCertificateIndex() {
            if (!this.token || this.viewer.isPreview) {
                this.certificateById = {};
                this.certificateByName = {};
                return;
            }
            try {
                const list = await apiCertificates.getPortalCertificates(this.token);
                const byId = {};
                const byName = {};
                (Array.isArray(list) ? list : []).forEach((entry) => {
                    const routeId = entry?.courseId ?? entry?.id;
                    if (routeId == null || routeId === '') return;
                    const record = {
                        routeId,
                        eligible: isTruthyFlag(entry.eligible),
                        issued: isTruthyFlag(entry.issued),
                    };
                    [entry.courseId, entry.id, entry.courseToken, entry.course?.id].forEach((key) => {
                        if (key != null && key !== '') byId[String(key)] = record;
                    });
                    const name = String(entry.courseName || entry.course?.name || '').trim().toLowerCase();
                    if (name) byName[name] = record;
                });
                this.certificateById = byId;
                this.certificateByName = byName;
            } catch (error) {
                this.certificateById = {};
                this.certificateByName = {};
            }
        },
        certificateRecord(item) {
            const keys = [item?.course?.id, item?.courseId, item?.id, item?.course?.courseId];
            for (const key of keys) {
                const record = key != null ? this.certificateById[String(key)] : null;
                if (record) return record;
            }
            const name = String(this.courseName(item) || '').trim().toLowerCase();
            return (name && this.certificateByName[name]) || null;
        },
        packageCertificateItem(group) {
            return {
                id: group?.id,
                courseId: group?.id,
                name: group?.name,
            };
        },
        groupIssuesCertificate(group) {
            return group?.certificateEnabled === true
                || group?.certificate === 1
                || group?.certificate === '1';
        },
        packageCertificateAction(group) {
            if (!this.groupIssuesCertificate(group)) return null;
            const record = this.certificateRecord(this.packageCertificateItem(group));
            if (!record) return null;
            if (record.issued) return 'issued';
            if (record.eligible) return 'eligible';
            return null;
        },
        certificateAction(item) {
            if (this.courseProgress(item) < 100) return null;
            const record = this.certificateRecord(item);
            if (!record) return null;
            if (record.issued) return 'issued';
            if (record.eligible) return 'eligible';
            return null;
        },
        isIssuingCertificate(item) {
            const record = this.certificateRecord(item);
            return record != null && String(this.issuingCourseId) === String(record.routeId);
        },
        viewCertificate(item) {
            const id = this.certificateRecord(item)?.routeId;
            if (id == null || id === '') return;
            this.$router.push(`/aluno/certificado/${id}`);
        },
        async generateCertificate(item) {
            const id = this.certificateRecord(item)?.routeId;
            if (id == null || id === '' || this.issuingCourseId != null) return;
            this.issuingCourseId = id;
            try {
                await apiCertificates.issueCertificate(this.token, id);
                await this.loadCertificateIndex();
                this.$router.push(`/aluno/certificado/${id}`);
            } catch (error) {
                toast.error(this.$t('student.corporatePortal.certificateIssueError'));
            } finally {
                this.issuingCourseId = null;
            }
        },
        openKeepWatching() {
            if (!this.keepWatching?.id) return;
            window.location.href = `/aluno/curso/${encodeURIComponent(this.keepWatching.id)}`;
        },
        courseKey(item) {
            return item?.course?.id || item?.id;
        },
        courseName(item) {
            return item?.course?.name || item?.name || this.$t('student.corporatePortal.courseFallback');
        },
        courseImage(item) {
            return item?.course?.imageFinalPath || item?.course?.imageFinalPath2 || null;
        },
        courseDescription(item) {
            const raw = item?.course?.shortDescription;
            if (!raw) return '';
            const text = String(raw).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            return text.length > 140 ? `${text.slice(0, 137)}…` : text;
        },
        courseProgress(item) {
            const raw = item?.course?.progressPercentage ?? item?.progress ?? 0;
            const n = Number(raw);
            if (Number.isNaN(n)) return 0;
            return Math.min(100, Math.max(0, Math.round(n)));
        },
        moduleQty(item) {
            const n = Number(item?.course?.moduleQty ?? item?.moduleQty);
            return Number.isNaN(n) ? null : n;
        },
        lessonQty(item) {
            const n = Number(item?.course?.lessonQty ?? item?.lessonQty);
            return Number.isNaN(n) ? null : n;
        },
        pickOrgValue(...values) {
            return values.find((value) => value !== undefined && value !== null && value !== '');
        },
        /**
         * Metadados de GET /portal/course: courses[] e groupCourses[].items[].
         */
        courseExtra(item) {
            const rawDate = item?.dateEnrol || item?.course?.dateEnrol;
            let assignedAt = '—';
            if (rawDate) {
                const parsed = new Date(rawDate);
                assignedAt = Number.isNaN(parsed.getTime())
                    ? String(rawDate)
                    : parsed.toLocaleDateString(this.$i18n?.locale || 'pt');
            }

            const days = this.pickOrgValue(item?.deadlineDays, item?.course?.deadlineDays);
            const deadlineDays = days == null ? null : Number(days);

            return {
                sector: this.pickOrgValue(item?.sectorName, item?.course?.sectorName) || '—',
                role: this.pickOrgValue(item?.roleName, item?.course?.roleName) || '—',
                responsible: this.pickOrgValue(
                    item?.responsibleName,
                    item?.course?.responsibleName,
                ) || '—',
                mandatory: Boolean(item?.mandatory ?? item?.course?.mandatory),
                deadline: deadlineDays != null && !Number.isNaN(deadlineDays)
                    ? this.$t('student.corporatePortal.daysDeadline', { days: deadlineDays })
                    : '—',
                assignedAt,
            };
        },
        imageStyle(item) {
            const image = this.courseImage(item);
            return image ? { backgroundImage: `url('${image}')` } : {};
        },
        statusLabel(item) {
            const p = this.courseProgress(item);
            if (p >= 100) return this.$t('student.courses.completed');
            if (p > 0) return this.$t('student.courses.inProgress');
            return this.$t('student.courses.notStarted');
        },
        statusClass(item) {
            const p = this.courseProgress(item);
            if (p >= 100) return 'is-done';
            if (p > 0) return 'is-progress';
            return 'is-pending';
        },
        progressTone(item) {
            const p = this.courseProgress(item);
            if (p >= 100) return 'is-high';
            if (p >= 40) return 'is-medium';
            return 'is-low';
        },
        courseId(item) {
            return moduleTokenFromEnrol(item, this.user?.type);
        },
        openCourse(item) {
            const token = this.courseId(item);
            if (!token) return;
            this.storeCurrentCoursePortal({ currentCoursePortal: item });
            window.location.href = `/aluno/curso/${encodeURIComponent(token)}`;
        },
    },
};
</script>

<style scoped>
.cp-dash {
    --cp-bg: #eef1f6;
    --cp-surface: #ffffff;
    --cp-text: #141824;
    --cp-muted: #667085;
    --cp-line: rgba(20, 24, 36, 0.08);
    --cp-primary: #250891;
    --cp-primary-soft: rgba(37, 8, 145, 0.08);
    --cp-teal: #14b8a6;
    --cp-course-bg: linear-gradient(180deg, #fbfcfe 0%, var(--cp-bg) 100%);
    --cp-course-nested-bg: #ffffff;
    --cp-package-bg: #f7f6fd;
    --cp-package-border: rgba(37, 8, 145, 0.14);
    --cp-package-divider: rgba(37, 8, 145, 0.1);
    --cp-course-hover-border: rgba(37, 8, 145, 0.28);
    --cp-course-hover-shadow: 0 10px 22px rgba(20, 24, 36, 0.06);
    min-height: 100%;
    background:
        radial-gradient(1200px 420px at 8% -10%, rgba(37, 8, 145, 0.08), transparent 60%),
        radial-gradient(900px 360px at 100% 0%, rgba(20, 184, 166, 0.08), transparent 55%),
        var(--cp-bg);
    padding: 1.5rem 0 3rem;
    text-align: left;
}

.cp-dash *:not(.cp-welcome__ring-label):not(.cp-welcome__ring-label *):not(.cp-state):not(.cp-state *) {
    text-align: inherit;
}

.cp-dash--dark {
    --cp-bg: #0f0e12;
    --cp-surface: #17161c;
    --cp-text: #f3f4f6;
    --cp-muted: #9ca3af;
    --cp-line: rgba(255, 255, 255, 0.08);
    --cp-primary-soft: rgba(167, 139, 250, 0.12);
    --cp-course-bg: #24222c;
    --cp-course-nested-bg: #24222c;
    --cp-package-bg: #131218;
    --cp-package-border: rgba(167, 139, 250, 0.22);
    --cp-package-divider: rgba(255, 255, 255, 0.08);
    --cp-course-hover-border: rgba(167, 139, 250, 0.45);
    --cp-course-hover-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
    background:
        radial-gradient(1000px 400px at 10% -10%, rgba(124, 58, 237, 0.18), transparent 60%),
        var(--cp-bg);
}

.cp-dash__wrap {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 20px;
}

.cp-welcome {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1.5rem;
    align-items: center;
    justify-items: start;
    background: var(--cp-surface);
    border: 1px solid var(--cp-line);
    border-radius: 22px;
    padding: 1.5rem 1.6rem;
    box-shadow: 0 10px 30px rgba(20, 24, 36, 0.04);
    margin-bottom: 1.1rem;
    text-align: left;
}

.cp-welcome__identity {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: flex-start;
    min-width: 0;
    width: 100%;
    text-align: left;
}

.cp-welcome__copy {
    min-width: 0;
    text-align: left;
}

.cp-welcome__avatar {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--cp-primary);
    background: var(--cp-primary-soft);
}

.cp-dash--dark .cp-welcome__avatar {
    color: #c4b5fd;
}

.cp-welcome__eyebrow {
    margin: 0 0 0.25rem;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--cp-primary);
    text-align: left;
}

.cp-dash--dark .cp-welcome__eyebrow {
    color: #c4b5fd;
}

.cp-welcome__copy h1 {
    margin: 0 0 0.35rem;
    font-size: clamp(1.5rem, 2.4vw, 1.9rem);
    color: var(--cp-text);
    line-height: 1.15;
    text-align: left;
}

.cp-welcome__lead {
    margin: 0;
    max-width: 38rem;
    color: var(--cp-muted);
    font-size: 0.92rem;
    line-height: 1.5;
    text-align: left;
}

.cp-welcome__ring {
    position: relative;
    width: 96px;
    height: 96px;
    justify-self: end;
}

.cp-welcome__ring-svg {
    width: 96px;
    height: 96px;
    transform: rotate(-90deg);
}

.cp-welcome__ring-track,
.cp-welcome__ring-value {
    fill: none;
    stroke-width: 8;
}

.cp-welcome__ring-track {
    stroke: rgba(37, 8, 145, 0.1);
}

.cp-dash--dark .cp-welcome__ring-track {
    stroke: rgba(255, 255, 255, 0.08);
}

.cp-welcome__ring-value {
    stroke: var(--cp-primary);
    stroke-linecap: round;
    transition: stroke-dashoffset 0.35s ease;
}

.cp-dash--dark .cp-welcome__ring-value {
    stroke: #a78bfa;
}

.cp-welcome__ring-label {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    text-align: center;
}

.cp-welcome__ring-label strong {
    font-size: 1.15rem;
    color: var(--cp-text);
    line-height: 1;
}

.cp-welcome__ring-label span {
    font-size: 0.68rem;
    color: var(--cp-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.cp-kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.85rem;
    margin-bottom: 1.25rem;
}

.cp-kpi {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.85rem;
    background: var(--cp-surface);
    border: 1px solid var(--cp-line);
    border-radius: 16px;
    padding: 1rem 1.05rem;
    text-align: left;
    width: 100%;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    font: inherit;
    color: inherit;
}

.cp-kpi:hover {
    border-color: rgba(37, 8, 145, 0.28);
    transform: translateY(-1px);
}

.cp-kpi.is-active {
    border-color: rgba(37, 8, 145, 0.45);
    box-shadow: 0 0 0 3px rgba(37, 8, 145, 0.1);
    background: #faf8ff;
}

.cp-dash--dark .cp-kpi.is-active {
    background: rgba(124, 58, 237, 0.12);
    border-color: rgba(167, 139, 250, 0.45);
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.16);
}

.cp-kpi__body {
    text-align: left;
}

.cp-kpi__icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    color: #fff;
}

.cp-kpi__icon :deep(svg),
.cp-kpi__icon :deep(svg path) {
    color: #fff !important;
    fill: currentColor !important;
}

.cp-kpi__icon--primary { background: #250891; color: #fff; }
.cp-kpi__icon--violet { background: #6d28d9; color: #fff; }
.cp-kpi__icon--green { background: #059669; color: #fff; }
.cp-kpi__icon--amber { background: #b45309; color: #fff; }

.cp-kpi__body span {
    display: block;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--cp-muted);
    margin-bottom: 0.15rem;
    text-align: left;
}

.cp-kpi__body strong {
    font-size: 1.45rem;
    color: var(--cp-text);
    line-height: 1;
    text-align: left;
}

.cp-section-head {
    margin-bottom: 0.9rem;
    text-align: left;
}

.cp-section-head h2 {
    margin: 0 0 0.2rem;
    font-size: 1.2rem;
    color: var(--cp-text);
    text-align: left;
}

.cp-section-head p {
    margin: 0;
    color: var(--cp-muted);
    font-size: 0.86rem;
    text-align: left;
}

.cp-section-head--row {
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: space-between;
    gap: 1rem;
    align-items: center;
}

.cp-section-head--row > div:first-child {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
}

.cp-continue {
    margin-bottom: 1.25rem;
}

.cp-continue__card {
    width: 100%;
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 0;
    padding: 0;
    border: 1px solid var(--cp-line);
    border-radius: 18px;
    overflow: hidden;
    background: var(--cp-surface);
    cursor: pointer;
    text-align: left;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.cp-continue__card:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px rgba(20, 24, 36, 0.08);
}

.cp-continue__media {
    min-height: 160px;
    background: #dbe3f5 center/cover no-repeat;
    position: relative;
}

.cp-continue__play {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: rgba(20, 184, 166, 0.95);
    color: #fff;
    box-shadow: 0 8px 20px rgba(20, 184, 166, 0.35);
}

.cp-continue__body {
    padding: 1.25rem 1.35rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 0.35rem;
    text-align: left;
}

.cp-continue__tag {
    align-self: flex-start;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6d28d9;
    background: rgba(124, 58, 237, 0.1);
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
}

.cp-continue__body h3 {
    margin: 0;
    font-size: 1.15rem;
    color: var(--cp-text);
    text-align: left;
}

.cp-continue__body p {
    margin: 0;
    color: var(--cp-muted);
    font-size: 0.88rem;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-align: left;
}

.cp-continue__cta {
    margin-top: 0.45rem;
    font-weight: 700;
    color: var(--cp-teal);
    font-size: 0.9rem;
    text-align: left;
}

.cp-courses {
    background: var(--cp-surface);
    border: 1px solid var(--cp-line);
    border-radius: 20px;
    padding: 1.25rem 1.3rem 1.4rem;
    text-align: left;
}

.cp-search {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 240px;
    min-width: 200px;
    flex: 0 0 auto;
    padding: 0.65rem 0.9rem;
    border-radius: 12px;
    border: 1px solid var(--cp-line);
    background: var(--cp-bg);
    color: var(--cp-muted);
}

.cp-search input {
    border: 0;
    outline: 0;
    width: 100%;
    min-width: 0;
    background: transparent;
    color: var(--cp-text);
    font-size: 0.9rem;
    text-align: left;
}

.cp-courses__controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
}

.cp-tabs {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem;
    border-radius: 12px;
    background: var(--cp-bg);
    border: 1px solid var(--cp-line);
}

.cp-tabs__btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    border: 0;
    background: transparent;
    color: var(--cp-muted);
    font-size: 0.86rem;
    font-weight: 650;
    padding: 0.45rem 0.75rem;
    border-radius: 9px;
    cursor: pointer;
}

.cp-tabs__btn.is-active {
    background: var(--cp-surface);
    color: var(--cp-text);
    box-shadow: 0 1px 2px rgba(20, 24, 36, 0.08);
}

.cp-tabs__count {
    min-width: 1.25rem;
    padding: 0.05rem 0.35rem;
    border-radius: 999px;
    background: rgba(20, 184, 166, 0.12);
    color: var(--cp-teal);
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
}

.cp-completion {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
}

.cp-completion__label {
    color: var(--cp-muted);
    font-size: 0.8rem;
    font-weight: 600;
}

.cp-completion select {
    appearance: none;
    border: 1px solid var(--cp-line);
    background: var(--cp-bg) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23667085' d='M1.4.8 6 5.4 10.6.8 12 2.2 6 8 0 2.2z'/%3E%3C/svg%3E") no-repeat right 0.75rem center;
    color: var(--cp-text);
    font-size: 0.86rem;
    font-weight: 650;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    border-radius: 10px;
    cursor: pointer;
}

.cp-course-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
}

.cp-course {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 1.1rem;
    align-items: stretch;
    justify-items: start;
    padding: 1.05rem 1.1rem;
    border-radius: 18px;
    border: 1px solid var(--cp-line);
    background: var(--cp-course-bg);
    cursor: pointer;
    transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
    text-align: left;
}

.cp-course__main {
    min-width: 0;
    width: 100%;
    text-align: left;
    justify-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
}

.cp-course:hover {
    border-color: var(--cp-course-hover-border);
    transform: translateY(-1px);
    box-shadow: var(--cp-course-hover-shadow);
}

.cp-course__media {
    width: 240px;
    height: 100%;
    min-height: 170px;
    border-radius: 16px;
    background: #dbe3f5 center/cover no-repeat;
    overflow: hidden;
}

.cp-course__placeholder {
    width: 100%;
    height: 100%;
    min-height: 170px;
    display: grid;
    place-items: center;
    color: #fff;
    background: #250891;
}

.cp-course__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 0.45rem;
    text-align: left;
}

.cp-course__title-row h3 {
    margin: 0;
    font-size: 1.05rem;
    color: var(--cp-text);
    text-align: left;
}

.cp-course__desc {
    margin: 0;
    color: var(--cp-muted);
    font-size: 0.84rem;
    line-height: 1.4;
    text-align: left;
}

.cp-course__org {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.55rem;
}

.cp-course__org-item {
    min-width: 0;
    padding: 0.55rem 0.65rem;
    border-radius: 10px;
    background: rgba(37, 8, 145, 0.04);
    border: 1px solid rgba(37, 8, 145, 0.06);
}

.cp-dash--dark .cp-course__org-item {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.06);
}

.cp-course__org-item span {
    display: block;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--cp-muted);
    margin-bottom: 0.15rem;
}

.cp-course__org-item strong {
    display: block;
    font-size: 0.82rem;
    color: var(--cp-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cp-course__progress-head {
    display: flex;
    justify-content: space-between;
    font-size: 0.72rem;
    color: var(--cp-muted);
    margin-bottom: 0.3rem;
    text-align: left;
}

.cp-course__progress-head strong {
    color: var(--cp-text);
}

.cp-course__track {
    height: 8px;
    border-radius: 999px;
    background: rgba(20, 24, 36, 0.08);
    overflow: hidden;
}

.cp-dash--dark .cp-course__track {
    background: rgba(255, 255, 255, 0.1);
}

.cp-course__fill {
    height: 100%;
    border-radius: inherit;
}

.cp-course__fill.is-high { background: #10b981; }
.cp-course__fill.is-medium { background: #250891; }
.cp-course__fill.is-low { background: #f59e0b; }

.cp-course__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1rem;
    margin-top: 0.15rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--cp-line);
}

.cp-course__meta {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 0.85rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--cp-text);
    text-align: left;
}

.cp-course__meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: #1f2937;
}

.cp-dash--dark .cp-course__meta span {
    color: #f3f4f6;
}

.cp-course__meta svg {
    color: #111827;
}

.cp-dash--dark .cp-course__meta svg {
    color: #fff;
}

.cp-course__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border: 0;
    border-radius: 12px;
    padding: 0.65rem 1.05rem;
    background: var(--cp-primary);
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    white-space: nowrap;
    margin-left: auto;
}

.cp-course__btn--certificate {
    background: #047857;
    color: #fff;
    border: 0;
    padding: 0.65rem 1.15rem;
    box-shadow: 0 1px 2px rgba(4, 120, 87, 0.25);
}

.cp-course__btn--certificate :deep(svg) {
    width: 0.95rem;
    height: 0.95rem;
}

.cp-course__btn--certificate:hover {
    background: #065f46;
    filter: none;
}

.cp-dash--dark .cp-course__btn--certificate {
    background: #059669;
    color: #fff;
}

.cp-dash--dark .cp-course__btn--certificate:hover {
    background: #047857;
}

.cp-course__btn:hover {
    filter: brightness(1.08);
}

.cp-chip {
    font-size: 0.68rem;
    font-weight: 700;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
}

.cp-chip.is-done {
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
}

.cp-chip.is-progress {
    background: rgba(37, 8, 145, 0.1);
    color: #250891;
}

.cp-chip.is-pending {
    background: rgba(100, 116, 139, 0.12);
    color: #475569;
}

.cp-chip.is-mandatory {
    background: rgba(180, 83, 9, 0.12);
    color: #b45309;
}

.cp-state {
    min-height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 0.45rem;
    color: var(--cp-muted);
    padding: 1.5rem;
}

.cp-state__icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    background: var(--cp-primary-soft);
    color: var(--cp-primary);
    margin-bottom: 0.25rem;
}

.cp-state h3 {
    margin: 0;
    color: var(--cp-text);
}

.cp-state p {
    margin: 0;
    max-width: 28rem;
    line-height: 1.45;
}

.cp-state__clear {
    margin-top: 0.35rem;
    border: 0;
    border-radius: 10px;
    padding: 0.55rem 0.9rem;
    background: var(--cp-primary);
    color: #fff;
    font-weight: 700;
    font-size: 0.82rem;
    cursor: pointer;
}

@media (max-width: 1100px) {
    .cp-course__org {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 960px) {
    .cp-welcome,
    .cp-kpis,
    .cp-continue__card,
    .cp-course {
        grid-template-columns: 1fr;
    }

    .cp-welcome__ring {
        justify-self: start;
    }

    .cp-section-head--row {
        flex-wrap: wrap;
        align-items: flex-start;
    }

    .cp-search {
        width: 100%;
        min-width: 0;
    }

    .cp-courses__controls {
        align-items: stretch;
    }

    .cp-tabs {
        width: 100%;
    }

    .cp-tabs__btn {
        flex: 1;
        justify-content: center;
    }

    .cp-completion {
        width: 100%;
        margin-left: 0;
    }

    .cp-completion select {
        flex: 1;
        min-width: 0;
    }

    .cp-course__media,
    .cp-continue__media {
        width: 100%;
        min-height: 200px;
        height: 200px;
    }

    .cp-course__org {
        grid-template-columns: 1fr;
    }

    .cp-course__footer {
        flex-direction: column;
        align-items: stretch;
    }

    .cp-course__btn {
        width: 100%;
        margin-left: 0;
    }
}

.cp-preview-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 1rem;
    padding: 0.85rem 1rem;
    border-radius: 14px;
    background: rgba(37, 99, 235, 0.12);
    color: #1e3a8a;
    font-size: 0.9rem;
}

.cp-preview-banner__exit {
    border: 0;
    border-radius: 10px;
    padding: 0.45rem 0.8rem;
    background: #1d4ed8;
    color: #fff;
    font-weight: 700;
    cursor: pointer;
}

.cp-package {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--cp-package-border);
    border-radius: 18px;
    background: var(--cp-package-bg);
    overflow: hidden;
    color: var(--cp-text);
}

.cp-package__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    text-align: left;
    border: 0;
    background: transparent;
    padding: 1rem 1.1rem;
    cursor: pointer;
    color: inherit;
}

.cp-package.is-open .cp-package__toggle {
    border-bottom: 1px solid var(--cp-package-divider);
}

.cp-package__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
}

.cp-package__title-row strong {
    font-size: 1rem;
    color: var(--cp-text);
}

.cp-package__certificate {
    display: flex;
    justify-content: flex-end;
    padding: 0 1rem 0.85rem;
}

.cp-package__lead {
    display: block;
    margin-top: 0.25rem;
    color: var(--cp-muted);
    font-size: 0.85rem;
}

.cp-package__courses {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.85rem;
}

.cp-package__courses .cp-course {
    background: var(--cp-course-nested-bg);
}

.cp-course.is-locked {
    cursor: default;
}

.cp-course.is-locked:hover {
    transform: none;
    box-shadow: none;
}

.cp-course__lock {
    margin: 0;
    color: #b45309;
    font-size: 0.85rem;
    font-weight: 600;
}

.cp-dash--dark .cp-course__lock {
    color: #fcd34d;
}

.cp-course__btn:disabled {
    background: #e5e7eb;
    color: #6b7280;
    cursor: not-allowed;
}

.cp-dash--dark .cp-course__btn:disabled {
    background: rgba(255, 255, 255, 0.08);
    color: #9ca3af;
}

.cp-chip.is-path {
    background: rgba(37, 8, 145, 0.1);
    color: #250891;
}

.cp-chip.is-group {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
}

.cp-chip.is-locked {
    background: rgba(100, 116, 139, 0.16);
    color: #475569;
}

.cp-dash--dark .cp-chip.is-done {
    background: rgba(16, 185, 129, 0.18);
    color: #6ee7b7;
}

.cp-dash--dark .cp-chip.is-progress {
    background: rgba(167, 139, 250, 0.18);
    color: #ddd6fe;
}

.cp-dash--dark .cp-chip.is-pending {
    background: rgba(148, 163, 184, 0.16);
    color: #e2e8f0;
}

.cp-dash--dark .cp-chip.is-mandatory {
    background: rgba(251, 191, 36, 0.16);
    color: #fcd34d;
}

.cp-dash--dark .cp-chip.is-path {
    background: rgba(167, 139, 250, 0.18);
    color: #ddd6fe;
}

.cp-dash--dark .cp-chip.is-group {
    background: rgba(52, 211, 153, 0.16);
    color: #6ee7b7;
}

.cp-dash--dark .cp-chip.is-locked {
    background: rgba(148, 163, 184, 0.16);
    color: #e2e8f0;
}

.cp-dash--dark .cp-course__fill.is-medium {
    background: #a78bfa;
}
</style>
